import { Link } from 'react-router-dom';
import { Briefcase, ArrowLeft } from 'lucide-react';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-6 hover:opacity-80"><ArrowLeft size={14} strokeWidth={1.5} /> Back to Home</Link>

        <h1 className="text-3xl md:text-4xl tracking-tight font-medium mb-2" style={{fontFamily:'Outfit'}}>Terms of Service</h1>
        <p className="text-sm text-[#1C2A24]/50 mb-8">Last updated: April 13, 2026</p>

        <div className="prose prose-sm max-w-none space-y-6 text-[#1C2A24]/80 leading-relaxed">
          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>1. Acceptance of Terms</h2>
            <p>By accessing or using WorkFromJoy ("the Platform"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Platform. We reserve the right to update these terms at any time, and continued use constitutes acceptance of changes.</p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>2. Description of Service</h2>
            <p>WorkFromJoy is an online platform that provides work-from-home job listings, career tools (including AI-powered resume building and practice interviews), community features, and employer services. We aggregate job listings from third-party sources and allow employers to post directly. We do not guarantee employment or the accuracy of third-party listings.</p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>3. User Accounts</h2>
            <p>To access certain features, you must create an account. You agree to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Provide accurate, current information during registration</li>
              <li>Maintain the security of your password and account</li>
              <li>Accept responsibility for all activity under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>
            <p className="mt-2">We reserve the right to suspend or terminate accounts that violate these terms or engage in fraudulent activity.</p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>4. Membership Plans & Payments</h2>
            <p>WorkFromJoy offers free and paid membership tiers. By subscribing to a paid plan, you agree to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Pay the applicable fees as displayed at the time of purchase</li>
              <li>Subscriptions are billed monthly and auto-renew unless cancelled</li>
              <li>Payments are processed securely through Stripe</li>
              <li>Refunds are handled on a case-by-case basis; contact support@workfromjoy.com</li>
              <li>We reserve the right to change pricing with 30 days notice</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>5. Employer / Business Accounts</h2>
            <p>Businesses using WorkFromJoy to post jobs agree to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Post only legitimate, real job opportunities</li>
              <li>Not post misleading, fraudulent, or scam listings</li>
              <li>Comply with all applicable employment and labor laws</li>
              <li>Not discriminate against applicants based on protected characteristics</li>
            </ul>
            <p className="mt-2">We reserve the right to remove any job listing or suspend any business account that violates these terms.</p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>6. User Content & Conduct</h2>
            <p>You are responsible for content you post, including community messages, resumes, and profile information. You agree not to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Post spam, offensive, or illegal content</li>
              <li>Harass, threaten, or abuse other users</li>
              <li>Attempt to scrape, hack, or interfere with the Platform</li>
              <li>Impersonate another person or entity</li>
              <li>Use the Platform for any unlawful purpose</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>7. Job Listings Disclaimer</h2>
            <p>WorkFromJoy aggregates job listings from third-party sources and employer submissions. While we make reasonable efforts to screen for scams and verify listings, we do not guarantee:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>The accuracy, completeness, or legitimacy of any job listing</li>
              <li>Employment outcomes or hiring decisions</li>
              <li>The conduct or reliability of any employer</li>
            </ul>
            <p className="mt-2">Users should independently verify employers before sharing personal information or accepting offers.</p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>8. Intellectual Property</h2>
            <p>All content, design, logos, and software on WorkFromJoy are owned by WorkFromJoy or its licensors. You may not copy, reproduce, distribute, or create derivative works without written permission.</p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>9. Limitation of Liability</h2>
            <p>WorkFromJoy is provided "as is" without warranties of any kind. To the maximum extent permitted by law, WorkFromJoy shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Platform, including but not limited to loss of income, data, or employment opportunities.</p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>10. Termination</h2>
            <p>We may terminate or suspend your access at any time, without notice, for conduct that we believe violates these Terms or is harmful to other users or the Platform. Upon termination, your right to use the Platform ceases immediately.</p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>11. Governing Law</h2>
            <p>These Terms shall be governed by and construed in accordance with the laws of the United States. Any disputes shall be resolved through binding arbitration or in the courts of applicable jurisdiction.</p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>12. Contact</h2>
            <p>For questions about these Terms, contact us at:</p>
            <p className="mt-2"><strong>Email:</strong> support@workfromjoy.com</p>
            <p><strong>Website:</strong> <Link to="/support" className="text-[#047857] hover:underline">WorkFromJoy Support</Link></p>
          </section>
        </div>
      </div>
    </div>
  );
}
